import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Button,
  Drawer,
  Group,
  NumberInput,
  Stack,
  Switch,
  TextInput,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import { isAxiosError } from 'axios';
import { IconShieldLock } from '@tabler/icons-react';
import type { PaymentMethodDto } from './types';
import { useCreatePaymentMethod, useUpdatePaymentMethod } from './paymentMethodsApi';

interface Props {
  opened: boolean;
  onClose: () => void;
  method: PaymentMethodDto | null;
}

interface FormValues {
  code: string;
  nameAr: string;
  nameEn: string;
  requiresAmount: boolean;
  isActive: boolean;
  sortOrder: number;
}

export function PaymentMethodFormDrawer({ opened, onClose, method }: Props) {
  const { t, i18n } = useTranslation();
  const create = useCreatePaymentMethod();
  const update = useUpdatePaymentMethod();
  const submitting = create.isPending || update.isPending;
  const editing = method !== null;
  const drawerPosition = i18n.language === 'ar' ? 'left' : 'right';

  const form = useForm<FormValues>({
    initialValues: {
      code: '',
      nameAr: '',
      nameEn: '',
      requiresAmount: true,
      isActive: true,
      sortOrder: 0,
    },
    validate: {
      code: (v) => (v.trim() ? null : t('common.errors.required')),
      nameAr: (v) => (v.trim() ? null : t('common.errors.required')),
    },
  });

  useEffect(() => {
    if (!opened) return;
    form.setValues({
      code: method?.code ?? '',
      nameAr: method?.nameAr ?? '',
      nameEn: method?.nameEn ?? '',
      requiresAmount: method?.requiresAmount ?? true,
      isActive: method?.isActive ?? true,
      sortOrder: method?.sortOrder ?? 0,
    });
    form.resetDirty();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened, method]);

  const submit = async (values: FormValues) => {
    const codeUpper = values.code.trim().toUpperCase();
    const basePayload = {
      code: codeUpper,
      nameAr: values.nameAr.trim(),
      nameEn: values.nameEn.trim() || null,
      requiresAmount: values.requiresAmount,
      sortOrder: values.sortOrder,
    };
    try {
      if (editing) {
        await update.mutateAsync({
          id: method.paymentMethodId,
          request: { ...basePayload, isActive: values.isActive },
        });
      } else {
        await create.mutateAsync(basePayload);
      }
      notifications.show({
        color: 'green',
        title: editing ? t('common.toasts.updated') : t('common.toasts.created'),
        message: basePayload.nameAr,
      });
      onClose();
    } catch (err) {
      notifications.show({
        color: 'red',
        title: t('common.toasts.errorTitle'),
        message: extractApiMessage(err) ?? t('common.toasts.errorBody'),
      });
    }
  };

  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      title={editing ? t('paymentMethods.form.editTitle') : t('paymentMethods.form.createTitle')}
      position={drawerPosition}
      size="md"
      padding="lg"
    >
      <form onSubmit={form.onSubmit(submit)}>
        <Stack gap="md">
          <Alert
            icon={<IconShieldLock size={16} stroke={1.6} />}
            color="gray"
            variant="light"
            radius="sm"
          >
            {t('paymentMethods.form.noProcessingNotice')}
          </Alert>

          <TextInput
            label={t('paymentMethods.form.codeLabel')}
            description={t('paymentMethods.form.codeHelp')}
            placeholder="CASH"
            withAsterisk
            {...form.getInputProps('code')}
            onChange={(e) =>
              form.setFieldValue('code', e.currentTarget.value.toUpperCase())
            }
          />
          <TextInput
            label={t('paymentMethods.form.nameArLabel')}
            placeholder={t('paymentMethods.form.nameArPlaceholder')}
            withAsterisk
            {...form.getInputProps('nameAr')}
          />
          <TextInput
            label={t('paymentMethods.form.nameEnLabel')}
            placeholder={t('paymentMethods.form.nameEnPlaceholder')}
            {...form.getInputProps('nameEn')}
          />
          <NumberInput
            label={t('paymentMethods.form.sortOrderLabel')}
            description={t('paymentMethods.form.sortOrderHelp')}
            min={0}
            step={1}
            {...form.getInputProps('sortOrder')}
          />
          <Switch
            label={t('paymentMethods.form.requiresAmountLabel')}
            description={t('paymentMethods.form.requiresAmountHelp')}
            checked={form.values.requiresAmount}
            onChange={(e) => form.setFieldValue('requiresAmount', e.currentTarget.checked)}
          />
          {editing && (
            <Switch
              label={t('paymentMethods.form.activeLabel')}
              description={t('paymentMethods.form.activeHelp')}
              checked={form.values.isActive}
              onChange={(e) => form.setFieldValue('isActive', e.currentTarget.checked)}
            />
          )}

          <Group justify="flex-end" gap="sm" mt="sm">
            <Button variant="default" onClick={onClose} disabled={submitting}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" loading={submitting}>
              {editing ? t('common.save') : t('common.create')}
            </Button>
          </Group>
        </Stack>
      </form>
    </Drawer>
  );
}

function extractApiMessage(err: unknown): string | null {
  if (isAxiosError(err)) {
    const data = err.response?.data as { detail?: string; title?: string } | undefined;
    return data?.detail ?? data?.title ?? err.message;
  }
  return null;
}
